import { Typography } from '@ghq-abi/design-system-v2';
import { CheckCircle2, Clock3, XCircle } from 'lucide-react';

export type BulkStatusTone = 'success' | 'failed' | 'pending';

export interface BulkStatusResultItemProps {
  label: string;
  statusLabel: string;
  tone: BulkStatusTone;
  secondaryText?: string;
  errorMessage?: string;
}

function StatusIcon({ tone }: { tone: BulkStatusTone }) {
  if (tone === 'success') {
    return (
      <CheckCircle2
        aria-hidden="true"
        className="h-4 w-4 shrink-0 text-green-600"
      />
    );
  }
  if (tone === 'failed') {
    return (
      <XCircle aria-hidden="true" className="h-4 w-4 shrink-0 text-red-600" />
    );
  }
  return (
    <Clock3 aria-hidden="true" className="h-4 w-4 shrink-0 text-yellow-500" />
  );
}

/** Shared list row for bulk approve / skip / reassign result modals. */
export function BulkStatusResultItem({
  label,
  statusLabel,
  tone,
  secondaryText,
  errorMessage
}: BulkStatusResultItemProps) {
  return (
    <div className="flex items-start justify-between rounded border border-gray-200 p-3">
      <div className="min-w-0">
        <Typography
          variant="body-sm-medium"
          color="default"
          as="div"
          className="truncate">
          {label}
        </Typography>
        {secondaryText ? (
          <Typography variant="metadata-sm-regular" color="light" as="div">
            {secondaryText}
          </Typography>
        ) : null}
        {errorMessage ? (
          <Typography variant="metadata-sm-regular" color="light" as="div">
            {errorMessage}
          </Typography>
        ) : null}
      </div>
      <div className="ml-3 flex shrink-0 items-center gap-2">
        <StatusIcon tone={tone} />
        <Typography variant="metadata-sm-bold" color="default" as="span">
          {statusLabel}
        </Typography>
      </div>
    </div>
  );
}

export function toBulkStatusTone(status: string): BulkStatusTone {
  if (status === 'SUCCESS' || status === 'APPROVED') {
    return 'success';
  }
  if (status === 'FAILED') {
    return 'failed';
  }
  return 'pending';
}
